import React, { useEffect } from 'react';
import { MessageSquare, X, User, BookOpen, Briefcase, Calendar, Clock, AlertTriangle } from 'lucide-react';
import { TeachingIntention } from '../../types/intentions';
import { displayLearningArea } from '../../constants/learningAreas';

interface AdditionalCommentsModalProps {
  isOpen: boolean;
  onClose: () => void;
  staffName: string;
  staffInitial: string;
  additionalComments: string;
  teachingIntention?: TeachingIntention | null;
}

const AdditionalCommentsModal: React.FC<AdditionalCommentsModalProps> = ({
  isOpen,
  onClose,
  staffName,
  staffInitial,
  additionalComments,
  teachingIntention
}) => {
  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape, true); // Use capture phase
      return () => document.removeEventListener('keydown', handleEscape, true);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      e.preventDefault();
      e.stopPropagation();
      onClose();
    }
  };

  const handleCloseClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onClose();
  };

  const handleModalClick = (e: React.MouseEvent) => {
    // Prevent any clicks inside the modal from bubbling up
    e.stopPropagation();
  };

  return (
    <div 
      className="fixed inset-0 z-[60] overflow-y-auto"
      onClick={handleOverlayClick}
    >
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Backdrop */}
        <div className="fixed inset-0 bg-ink-500 bg-opacity-75 transition-opacity" />
        
        {/* Modal */}
        <div 
          className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl w-full"
          onClick={handleModalClick}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-ink-200">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-navy-100 rounded-full">
                <User className="h-5 w-5 text-navy-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-ink-900">
                  Teacher Information
                </h3>
                <p className="text-sm text-ink-500">
                  {staffInitial} - {staffName}
                </p>
              </div>
            </div>
            <button
              onClick={handleCloseClick}
              className="p-2 hover:bg-ink-100 rounded-full transition-colors"
              title="Close"
            >
              <X className="h-5 w-5 text-ink-500" />
            </button>
          </div>

          {/* Content - Single Page Layout */}
          <div className="p-6 space-y-6">
            {/* Teaching Preferences Section */}
            <div>
              <h4 className="text-lg font-medium text-ink-900 mb-4 flex items-center">
                <BookOpen className="h-5 w-5 mr-2 text-navy-600" />
                Teaching Preferences
              </h4>
              {teachingIntention?.teachingPreferences && teachingIntention.teachingPreferences.length > 0 ? (
                <div className="space-y-3">
                  {teachingIntention.teachingPreferences.map((pref, index) => (
                    <div key={index} className="bg-ink-50 rounded-lg p-3 border border-ink-200">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <span className="bg-navy-100 text-navy-800 text-xs font-medium px-2 py-1 rounded-full font-mono tabular-nums">
                              #{index + 1}
                            </span>
                            <h5 className="text-sm font-medium text-ink-900">
                              {typeof pref === 'string' ? pref : (pref.specificSubject || pref.learningArea || 'Not specified')}
                            </h5>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 bg-ink-50 rounded-lg border border-ink-200">
                  <BookOpen className="h-8 w-8 text-ink-300 mx-auto mb-2" />
                  <p className="text-sm text-ink-500">No teaching preferences submitted</p>
                </div>
              )}

              {teachingIntention?.comfortableTeachingAreas && teachingIntention.comfortableTeachingAreas.length > 0 && (
                <div className="mt-4">
                  <h5 className="text-sm font-medium text-ink-900 mb-2">Comfortable Teaching Areas</h5>
                  <div className="flex flex-wrap gap-2">
                    {teachingIntention.comfortableTeachingAreas.map((area, index) => (
                      <span key={index} className="bg-ok-bg text-ok-fg text-xs font-medium px-2 py-1 rounded-full">
                        {area}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Comments Section */}
            <div>
              <h4 className="text-lg font-medium text-ink-900 mb-4 flex items-center">
                <MessageSquare className="h-5 w-5 mr-2 text-navy-600" />
                Additional Comments
              </h4>
              {additionalComments ? (
                <div className="bg-ink-50 rounded-lg p-4 border border-ink-200">
                  <p className="text-sm text-ink-700 whitespace-pre-wrap leading-relaxed">
                    {additionalComments}
                  </p>
                </div>
              ) : (
                <div className="text-center py-6 bg-ink-50 rounded-lg border border-ink-200">
                  <MessageSquare className="h-8 w-8 text-ink-300 mx-auto mb-2" />
                  <p className="text-sm text-ink-500">No additional comments provided</p>
                </div>
              )}
            </div>

            {/* Employment Information Section */}
            <div>
              <h4 className="text-lg font-medium text-ink-900 mb-4 flex items-center">
                <Briefcase className="h-5 w-5 mr-2 text-navy-600" />
                Employment & Availability
              </h4>
              {teachingIntention ? (
                <div className="space-y-4">
                  {/* Employment Details - New Structure */}
                  {teachingIntention.employmentDetails && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-ink-50 rounded-lg p-4 border border-ink-200">
                        <h5 className="text-sm font-medium text-ink-900 mb-2">Employment Status</h5>
                        <div className="space-y-2 text-sm text-ink-700">
                          <p>
                            <span className="font-medium">Type:</span>{' '}
                            <span className="capitalize">{teachingIntention.employmentDetails.employmentType}</span>
                          </p>
                          {teachingIntention.employmentDetails.employmentType === 'ongoing' ? (
                            <p>
                              <span className="font-medium">Returning:</span>{' '}
                              <span className={
                                teachingIntention.employmentDetails.intentionToReturn === 'yes' ? 'text-ok-fg font-medium' :
                                teachingIntention.employmentDetails.intentionToReturn === 'no' ? 'text-danger-fg font-medium' :
                                'text-warn-fg font-medium'
                              }>
                                {teachingIntention.employmentDetails.intentionToReturn === 'yes' ? 'Yes' :
                                 teachingIntention.employmentDetails.intentionToReturn === 'no' ? 'No' : 'Uncertain'}
                              </span>
                            </p>
                          ) : (
                            <p>
                              <span className="font-medium">Seeking employment:</span>{' '}
                              <span className={
                                teachingIntention.employmentDetails.wishToBeConsidered === 'yes' ? 'text-ok-fg font-medium' :
                                teachingIntention.employmentDetails.wishToBeConsidered === 'no' ? 'text-danger-fg font-medium' :
                                'text-warn-fg font-medium'
                              }>
                                {teachingIntention.employmentDetails.wishToBeConsidered === 'yes' ? 'Yes' :
                                 teachingIntention.employmentDetails.wishToBeConsidered === 'no' ? 'No' : 'Uncertain'}
                              </span>
                            </p>
                          )}
                          {teachingIntention.employmentDetails.notReturningReason && (
                            <p className="text-ink-500 italic text-xs mt-1">
                              "{teachingIntention.employmentDetails.notReturningReason}"
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Leave Plans */}
                      {teachingIntention.employmentDetails.hasLeavePlans && (
                        <div className="bg-navy-50 rounded-lg p-4 border border-navy-200">
                          <h5 className="text-sm font-medium text-navy-900 mb-2 flex items-center">
                            <Calendar className="h-4 w-4 mr-1" />
                            Leave Planned
                          </h5>
                          <div className="space-y-1 text-sm text-navy-700">
                            <p>
                              <span className="font-medium">Type:</span>{' '}
                              {teachingIntention.employmentDetails.leaveType === 'long-service' ? 'Long Service Leave' :
                               teachingIntention.employmentDetails.leaveType === 'leave-without-pay' ? 'Leave Without Pay' :
                               'Other Leave'}
                            </p>
                            {teachingIntention.employmentDetails.leaveStartDate && (
                              <p><span className="font-medium">From:</span> {teachingIntention.employmentDetails.leaveStartDate}</p>
                            )}
                            {teachingIntention.employmentDetails.leaveEndDate && (
                              <p><span className="font-medium">To:</span> {teachingIntention.employmentDetails.leaveEndDate}</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Availability - New Structure */}
                  {teachingIntention.availability && (
                    <div className="bg-navy-50 rounded-lg p-4 border border-navy-200">
                      <h5 className="text-sm font-medium text-navy-900 mb-3 flex items-center">
                        <Clock className="h-4 w-4 mr-1" />
                        Availability
                      </h5>
                      <div className="space-y-3">
                        {/* FTE */}
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-navy-800">Intended FTE:</span>
                          <span className="px-2 py-1 bg-navy-100 text-navy-800 rounded font-medium font-mono tabular-nums">
                            {(teachingIntention.availability.fte * 100).toFixed(0)}%
                          </span>
                          <span className="text-sm text-navy-600 font-mono tabular-nums">
                            ({teachingIntention.availability.fte.toFixed(2)})
                          </span>
                        </div>

                        {/* Days Available */}
                        {teachingIntention.availability.daysAvailable && teachingIntention.availability.daysAvailable.length > 0 && (
                          <div>
                            <span className="text-sm font-medium text-navy-800">Days Available:</span>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {['monday', 'tuesday', 'wednesday', 'thursday', 'friday'].map(day => {
                                const isAvailable = teachingIntention.availability?.daysAvailable?.includes(day);
                                return (
                                  <span
                                    key={day}
                                    className={`px-2 py-1 text-xs rounded font-medium ${
                                      isAvailable
                                        ? 'bg-ok-bg text-ok-fg border border-ok-border'
                                        : 'bg-ink-100 text-ink-400 border border-ink-200 line-through'
                                    }`}
                                  >
                                    {day.charAt(0).toUpperCase() + day.slice(1, 3)}
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Periods Unavailable */}
                        {teachingIntention.availability.periodsUnavailable && teachingIntention.availability.periodsUnavailable.length > 0 && (
                          <div>
                            <span className="text-sm font-medium text-warn-fg flex items-center">
                              <AlertTriangle className="h-3 w-3 mr-1" />
                              Specific Periods Unavailable:
                            </span>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {teachingIntention.availability.periodsUnavailable.slice(0, 10).map((period, idx) => (
                                <span
                                  key={idx}
                                  className="px-2 py-0.5 text-xs bg-danger-bg text-danger-fg rounded"
                                >
                                  {period.day.charAt(0).toUpperCase() + period.day.slice(1, 3)} P{period.period}
                                </span>
                              ))}
                              {teachingIntention.availability.periodsUnavailable.length > 10 && (
                                <span className="text-xs text-ink-500">
                                  +{teachingIntention.availability.periodsUnavailable.length - 10} more
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Special Role Interest */}
                  {teachingIntention.employmentDetails &&
                    teachingIntention.employmentDetails.year9StretchInterest !== null && (
                      <div className="bg-warn-bg rounded-lg p-4 border border-warn-border">
                        <h5 className="text-sm font-medium text-warn-fg mb-2">Special Role Interest</h5>
                        <div className="space-y-1 text-sm">
                          <p className={teachingIntention.employmentDetails.year9StretchInterest ? 'text-ok-fg' : 'text-ink-500'}>
                            Year 9 Stretch Program: {teachingIntention.employmentDetails.year9StretchInterest ? '✓ Interested' : '✗ Not interested'}
                          </p>
                        </div>
                      </div>
                    )}

                  {/* TIL Activities */}
                  {teachingIntention.employmentDetails?.tilActivities && teachingIntention.employmentDetails.tilActivities.length > 0 && (
                    <div className="bg-navy-50 rounded-lg p-4 border border-navy-200">
                      <h5 className="text-sm font-medium text-navy-900 mb-2">
                        TIL Activities ({teachingIntention.employmentDetails.tilActivities.length} selected)
                      </h5>
                      <div className="flex flex-wrap gap-1">
                        {teachingIntention.employmentDetails.tilActivities.map((activity, idx) => (
                          <span key={idx} className="px-2 py-1 text-xs bg-navy-100 text-navy-700 rounded">
                            {activity}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Legacy fields fallback - only show if new structure not present */}
                  {!teachingIntention.employmentDetails && !teachingIntention.availability && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-ink-50 rounded-lg p-4 border border-ink-200">
                        <h5 className="text-sm font-medium text-ink-900 mb-2">Current Employment</h5>
                        <div className="space-y-2 text-sm text-ink-700">
                          <p><span className="font-medium">Type:</span> {teachingIntention.currentEmploymentType}</p>
                          <p><span className="font-medium">FTE:</span> {teachingIntention.currentFTE}</p>
                          <p><span className="font-medium">Learning Area:</span> {displayLearningArea(teachingIntention.department)}</p>
                        </div>
                      </div>

                      <div className="bg-ink-50 rounded-lg p-4 border border-ink-200">
                        <h5 className="text-sm font-medium text-ink-900 mb-2">Future Intentions</h5>
                        <div className="space-y-2 text-sm text-ink-700">
                          <p><span className="font-medium">Intention to return:</span> {teachingIntention.intentionToReturn}</p>
                          <p><span className="font-medium">Desired status:</span> {teachingIntention.desiredEmploymentStatus}</p>
                          <p><span className="font-medium">Target FTE:</span> {teachingIntention.targetFTE}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {teachingIntention.preferredDays && (
                    <div className="bg-ink-50 rounded-lg p-4 border border-ink-200">
                      <h5 className="text-sm font-medium text-ink-900 mb-2">Preferred Working Days (Legacy)</h5>
                      <p className="text-sm text-ink-700">{teachingIntention.preferredDays}</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-6 bg-ink-50 rounded-lg border border-ink-200">
                  <Briefcase className="h-8 w-8 text-ink-300 mx-auto mb-2" />
                  <p className="text-sm text-ink-500">No employment information submitted</p>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end p-6 border-t border-ink-200 bg-ink-50">
            <button
              onClick={handleCloseClick}
              className="px-4 py-2 bg-ink-500 text-white rounded-lg hover:bg-ink-700 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdditionalCommentsModal;